import React, { useState } from 'react';
import {
  Box, Typography, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress,
  Alert, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import accountsApiService from '../../services/accountsApiService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/reactApi/api';

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹ 0.00';
  return `₹ ${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const OutstandingAmount = () => {
  const [customerNo, setCustomerNo] = useState('');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const [downloadingInvoice, setDownloadingInvoice] = useState(null);

  const handleSearch = async () => {
    if (!customerNo.trim()) {
      setError('Enter a username / customer number');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setSearched(false);
      const response = await accountsApiService.getOutstandingAmount(customerNo.trim());
      setData(response.value || []);
      setSearched(true);
    } catch (err) {
      setError(err.message || 'Failed to fetch outstanding amounts');
    } finally {
      setLoading(false);
    }
  };

  const handleViewInvoice = async (billreference) => {
    try {
      setDownloadingInvoice(billreference);
      const url = `${API_BASE_URL}/accounts/business-central/invoice?invoiceNo=${encodeURIComponent(billreference)}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Invoice not available');
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `Invoice_${billreference.replace(/[^a-zA-Z0-9]/g, '-')}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (err) {
      alert('Failed to download invoice: ' + err.message);
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const unpaidData = data.filter(row => row.PaymentStatus !== 'Paid' && (row.BillOpeningBalance || 0) >= 0);
  const totalClosingBalance = unpaidData.reduce((sum, row) => sum + (row.BillClosingBalance || 0), 0);
const netClosingBalance = data.reduce((sum, row) => sum + (row.BillClosingBalance || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: '#00024c', mb: 3 }}>
        Outstanding Amount Lookup
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <TextField
          label="Username / Customer No"
          value={customerNo}
          onChange={(e) => setCustomerNo(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          placeholder="e.g. Nabln000060"
          sx={{ width: 320 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
          }}
        />
        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={loading}
          sx={{ bgcolor: '#00024c', '&:hover': { bgcolor: '#000380' }, px: 4, height: 56 }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Search'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {searched && data.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">No outstanding amounts found for "{customerNo}".</Typography>
        </Paper>
      )}

      {data.length > 0 && (
        <>
          <TableContainer component={Paper} elevation={2}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#00024c' }}>
                  {['S.No', 'Bill Reference', 'Bill Date', 'Party Name', 'Lab ID', 'Opening Balance', 'Closing Balance', 'Due Date', 'Overdue Days', 'Payment Status', 'Invoice'].map((h) => (
                    <TableCell key={h} sx={{ color: 'white', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => (
                  <TableRow key={row.Entry_No} sx={{ bgcolor: index % 2 === 0 ? '#f5f5f5' : 'white' }}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{row.Billreference}</TableCell>
                    <TableCell>{formatDate(row.BillDate)}</TableCell>
                    <TableCell>{row.BillPartyName}</TableCell>
                    <TableCell>{row.Labid}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{formatCurrency(row.BillOpeningBalance)}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{formatCurrency(row.BillClosingBalance)}</TableCell>
                    <TableCell>{formatDate(row.BillDueOn)}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${row.BillOverDueDays} days`}
                        size="small"
                        color={row.BillOverDueDays > 30 ? 'error' : row.BillOverDueDays > 0 ? 'warning' : 'success'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.PaymentStatus || 'Not Paid'}
                        size="small"
                        color={row.PaymentStatus === 'Paid' ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        disabled={downloadingInvoice === row.Billreference}
                        onClick={() => handleViewInvoice(row.Billreference)}
                        sx={{ fontSize: '0.75rem' }}
                      >
                        {downloadingInvoice === row.Billreference ? 'Loading...' : 'Invoice'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                  <TableCell colSpan={6} sx={{ fontWeight: 700, textAlign: 'right', fontSize: '1rem' }}>
                    Total Payment Amount (Unpaid):
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '1.1rem', color: netClosingBalance > 0 ? '#d32f2f' : '#2e7d32'  }}>
                    {formatCurrency(netClosingBalance)}
                  </TableCell>
                  <TableCell colSpan={4} />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default OutstandingAmount;